import { EchartsWithTheme, Empty, useTheme, Select, Radio } from '@maxtropy/components';
import React, { useEffect, useMemo, useState } from 'react';
import { DateType, MeasurementType, getCommonMarkPint } from '../utils';
import tittleDecoration from '../tittleDecoration.png';
import ResizeObserver from 'rc-resize-observer';
import styles from './index.module.scss';
import { EnergyMonitorContext } from '../';
import { Spin, Space } from 'antd';
import { isEmpty, isNil } from 'lodash-es';
import dayjs from 'dayjs';
import {
  apiV2HighEnergyDeviceMonitoringOfKeyIndicatorsPost,
  V2HighEnergyDeviceMonitoringOfKeyIndicatorsPostResponse,
  apiV2HighEnergyDeviceTotalIndicatorPost,
} from '@maxtropy/device-customer-apis-v2';
import { keepTwoNull } from '@/utils/utils';
import useLatestRequest from '../useLatestRequest';

const COLOR_SCHEMES = [
  { border: '#16DD8E' },
  { border: '#ffc107' },
  { border: '#007bff' },
  { border: '#dc3545' },
  { border: '#17a2b8' },
];

type InfoListItem = NonNullable<V2HighEnergyDeviceMonitoringOfKeyIndicatorsPostResponse['infoList']>[number];

interface KpiNormProps {
  timeResolution?: string;
  displayValue?: any;
  timeRangeTidy?: number[];
  dataQueryPeriodId?: number;
}
interface IndicatorItem {
  label: string;
  value: number;
  propertyId: string;
  groupType: number;
}
interface Option {
  label: string;
  value: string;
}
const NOGROUP = 0;
const KpiNorm: React.FC<KpiNormProps> = ({ timeResolution, timeRangeTidy, dataQueryPeriodId }) => {
  const theme = useTheme();
  const { deviceLedgerId } = React.useContext(EnergyMonitorContext);
  const [checkedList, setCheckedList] = useState<number>();
  const [datas, setDatas] = useState<InfoListItem[]>([]);
  const [headTitle, setHeadTitle] = useState<IndicatorItem[]>([]);
  const [indicatorList, setIndicatorList] = useState<IndicatorItem[]>([]);
  const [boxplotEchart, setBoxplotEchart] = useState<InfoListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [isShow, setIsShow] = useState<boolean>(false);
  const { runLatest: runLatestIndicators, cancelLatest: cancelLatestIndicators } = useLatestRequest();
  const { runLatest: runLatestData, cancelLatest: cancelLatestData } = useLatestRequest();

  useEffect(() => {
    setCompareIds([]);
    setCheckedList(undefined);
    setHeadTitle([]);
    setIndicatorList([]);
    setOptions([]);
    setIsShow(false);
    if (!deviceLedgerId) {
      cancelLatestIndicators();
      return;
    }
    runLatestIndicators(() => apiV2HighEnergyDeviceTotalIndicatorPost({ id: deviceLedgerId }), {
      onSuccess: res => {
        const nextIndicatorList = res.indicatorDetail?.flatMap(item => {
          const result = item.propertyList?.map(i => i.propertyId).join('');
          const resultid = (item.propertyList ?? []).map(i => i.propertyId!).join('-');

          if (item.groupType !== NOGROUP) {
            return [
              {
                label: item?.showName!,
                value: Number(result),
                propertyId: resultid,
                groupType: item.groupType,
              },
            ];
          } else {
            return item.propertyList?.map(p => ({
              label: item?.showName!,
              value: p?.propertyId,
              propertyId: String(p?.propertyId),
              groupType: p.groupType,
            }));
          }
        }) as IndicatorItem[];

        if (nextIndicatorList?.[0]?.groupType === NOGROUP) {
          const res = nextIndicatorList.filter(i => i.groupType === NOGROUP).slice(1);
          const ops = res.map(i => ({ label: i.label, value: i.propertyId }));
          setOptions(ops);
          setIsShow(true);
        }

        setCheckedList(nextIndicatorList?.[0]?.value);
        setHeadTitle(nextIndicatorList ?? []);
        setIndicatorList(nextIndicatorList ?? []);
      },
    });
  }, [cancelLatestIndicators, deviceLedgerId, runLatestIndicators]);

  useEffect(() => {
    if (!deviceLedgerId || !checkedList || !timeRangeTidy) {
      cancelLatestData();
      setDatas([]);
      setBoxplotEchart([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    runLatestData(
      () =>
        apiV2HighEnergyDeviceMonitoringOfKeyIndicatorsPost({
          deviceLedgerId,
          timeResolution,
          startTime: timeRangeTidy[0],
          endTime: timeRangeTidy[1],
          dataQueryPeriodId,
        }),
      {
        onSuccess: res => {
          setDatas(res.infoList ?? []);

          const ids = indicatorList.filter(i => checkedList === i.value);
          const checkedIndicator = extractPropertyIds(ids);
          const checkRes = (res.infoList ?? []).filter((item: InfoListItem) =>
            checkedIndicator.includes(item.datapropertyId!)
          );

          setBoxplotEchart(checkRes);
        },
        onSettled: () => setLoading(false),
      }
    );
  }, [
    cancelLatestData,
    checkedList,
    dataQueryPeriodId,
    deviceLedgerId,
    indicatorList,
    runLatestData,
    timeRangeTidy,
    timeResolution,
  ]);

  const onChange = (e: any) => {
    const list = e.target.value;
    setCompareIds([]);
    setCheckedList(list);
    const ids = indicatorList.filter(i => list === i.value);

    if (ids[0].groupType === NOGROUP) {
      const res = indicatorList.filter(i => i.groupType === NOGROUP && list !== i.value);
      const ops = res.map(i => ({ label: i.label, value: i.propertyId }));
      setOptions(ops);
      setIsShow(true);
    } else {
      setIsShow(false);
    }

    const checkedIndicator = extractPropertyIds(ids);
    const checkRes = (datas ?? []).filter((item: InfoListItem) => checkedIndicator.includes(item.datapropertyId!));

    setBoxplotEchart(checkRes);
  };

  const onChangeSelect = (e: any) => {
    setCompareIds(e);
    const gather: string[] = [].concat(e);
    const id = indicatorList.find(i => checkedList === i.value);
    gather.push(id?.propertyId ?? '');
    const checkedIndicator = gather.map(i => Number(i));
    const checkRes = (datas ?? []).filter((item: InfoListItem) => checkedIndicator.includes(item.datapropertyId!));

    setBoxplotEchart(checkRes);
  };

  const formatBoxplotData = (list: any[] = [], isDay?: boolean) => {
    const boxData: number[][] = [];
    const lineData: Array<number | number[]> = [];
    const outliers: any[] = [];

    list.forEach((item: any, index: number) => {
      boxData.push([item.min, item.q1, item.median, item.q3, item.max]);

      lineData.push(isDay ? [item.time, item.value] : item.value);
      // 异常值处理
      if (item.abnormal && item.abnormal.length > 0) {
        item.abnormal.forEach((val: any) => {
          outliers.push([index, val]);
        });
      }
    });

    return { boxData, lineData, outliers };
  };

  const xLabel = useMemo(() => {
    if (timeResolution === DateType.MONTH || timeResolution === DateType.WEEK) {
      return 'YYYY-MM-DD';
    } else if (timeResolution === DateType.YEAR) {
      return 'YYYY-MM';
    } else {
      return 'HH:mm';
    }
  }, [timeResolution]);

  const baseSeriesOption = (seriesInfo: InfoListItem[], isDay?: boolean) => {
    const resdata: any[] = [];
    seriesInfo.map((item: InfoListItem, index: number) => {
      const validMarkLineData = [];
      const colorIndex = index % COLOR_SCHEMES.length;
      const { border } = COLOR_SCHEMES[colorIndex];

      if (item.avg !== undefined && item.avg !== null && !isNaN(Number(item.avg))) {
        validMarkLineData.push({
          name: '平均值',
          yAxis: item.avg,
          lineStyle: {
            color: border,
            type: 'dashed',
          },
        });
      }

      if (item.value !== undefined && item.value !== null && !isNaN(Number(item.value))) {
        validMarkLineData.push({
          name: '自定义值',
          yAxis: item.value,
          lineStyle: {
            color: border,
            type: 'solid',
          },
        });
      }

      if (timeResolution !== DateType.DAY && item.type === MeasurementType.INSTANT) {
        const { boxData } = formatBoxplotData(item.dataInfoList);
        resdata.push({
          name: `${item.dataName}`,
          type: 'boxplot',
          yAxisIndex: index,
          data: boxData,
          itemStyle: {
            color: border,
            borderColor: border,
            borderWidth: 1,
          },
          ...(item.value && {
            markLine: {
              symbol: ['none', 'none'],
              label: {
                show: true,
                position: 'insideEndTop',
                color: border,
                formatter: () => {
                  return `${item.name}: ${item.value?.toFixed(2)}${item.generalUnitName}`;
                },
              },
              lineStyle: {
                width: 2,
              },
              data: [
                {
                  name: '自定义值',
                  yAxis: item.value,
                  lineStyle: {
                    color: border,
                    type: 'solid',
                  },
                },
              ],
            },
          }),
        });
      } else {
        resdata.push({
          name: `${item.dataName}`,
          type: 'line',
          connectNulls: true,
          // stack: 'Total',
          yAxisIndex: index,
          smooth: true,
          data: formatBoxplotData(item.dataInfoList, isDay).lineData,
          lineStyle: {
            width: 2,
            color: border,
          },
          markPoint: getCommonMarkPint(item.indicatorUnitName),
          markLine: {
            symbol: ['none', 'none'],
            label: {
              show: true,
              position: 'insideEndTop',
              color: border,
              formatter: (params: any) => {
                if (params.name === '平均值') {
                  return `平均值: ${item.avg?.toFixed(2)}${item.indicatorUnitName}`;
                } else {
                  return `${item.name}: ${item.value?.toFixed(2)}${item.generalUnitName}`;
                }
              },
            },
            lineStyle: {
              width: 2,
            },
            data: validMarkLineData,
          },
        });
      }
    });

    return resdata;
  };

  const getSafeYAxis = (boxplotEchart: InfoListItem[]) => {
    if (boxplotEchart && boxplotEchart.length > 0) {
      const len = boxplotEchart.length;
      const isSpecialCase = len <= 2 || boxplotEchart.every(item => item.type === boxplotEchart[0].type);

      const typeCount = {
        1: boxplotEchart.filter(item => item.type === 1).length,
        2: boxplotEchart.filter(item => item.type === 2).length,
      };
      return boxplotEchart.map((item: any, index: number) => {
        const { boxData, lineData } = formatBoxplotData(item.dataInfoList);
        let allValues: number[] = [];
        if (timeResolution !== DateType.DAY && item.type === MeasurementType.INSTANT) {
          if (boxData?.length) {
            boxData.forEach(arr => {
              allValues.push(...arr.filter(v => v !== null && typeof v === 'number'));
            });
          }
        } else {
          //如[min, q1, median, q3, max]）
          allValues = lineData.filter((val): val is number => val !== null && typeof val === 'number');
        }
        if (item.value) allValues.push(item.value as number);
        const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
        const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;

        const padding = (maxValue - minValue) * 0.3;
        const type = item.type as 1 | 2;
        const position = typeCount[type] === 2 ? 'right' : 'left';

        // if (index === 0) {
        return {
          type: 'value',
          name: item.indicatorUnitName || '',
          position: isSpecialCase ? (index === 0 ? 'left' : 'right') : position,
          offset: index > 1 ? (index - 1) * 55 : 0,
          axisLabel: { formatter: (value: number) => value.toFixed(2) },
          alignTicks: true,
          min: minValue - padding, // 自动取所有箱线数据的最小值
          max: maxValue + padding,
        };
        // } else {
        //   return {
        //     type: 'value',
        //     name: item.indicatorUnitName || '',
        //     position: isSpecialCase ? (index === 0 ? 'left' : 'right') : position,
        //     offset: index > 1 ? (index - 1) * 55 : 0,
        //     axisLabel: { formatter: (value: number) => value.toFixed(2) },
        //     alignTicks: true,
        //     // min: minValue - padding, // 自动取所有箱线数据的最小值
        //     // max: maxValue + padding,
        //   };
        // }
      });
    }

    return [
      {
        type: 'value',
        show: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },
    ];
  };

  const dayChartOption = () => {
    return {
      grid: {
        top: 60,
        left: 40,
        right: 50,
        containLabel: true,
      },
      legend: {
        type: 'scroll',
        right: 10,
        itemGap: 16,
        data: boxplotEchart.map(s => {
          const isBar = timeResolution !== DateType.DAY && s.type === MeasurementType.INSTANT;
          return {
            name: s.dataName,
            icon: isBar
              ? 'path://M0 0 L12 0 L12 12 L0 12 Z' // 方块 (12x12)
              : 'path://M0 4 L12 4 L12 8 L0 8 Z', // 扁条 (12x4)
          };
        }),
      },
      color: ['#16DD8E', '#ffc107', '#007bff'],
      tooltip: {
        formatter: (items: any) => {
          let { axisValue } = items[0];
          let firstStr = dayjs(axisValue).format('YYYY-MM-DD HH:mm');
          let otherStr = '';
          let str = '';

          items.forEach((item: any) => {
            const { seriesName, data, marker } = item;
            const basic = boxplotEchart.find(i => i.dataName === seriesName);
            const value = !isNil(data[1]) ? `${data[1].toFixed(2)}${basic?.indicatorUnitName}` : '--';

            const basicValue = !isNil(basic?.value) ? `${basic?.value.toFixed(2)}${basic?.generalUnitName}` : '--';
            otherStr += `<div style="margin-top:8px;width:200px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${value}</div></div>`;

            if (basic?.name) {
              otherStr += `<div style="margin-top:8px;width:200px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div>${basic?.name}</div><div>${basicValue}</div></div>`;
            }

            str = firstStr + otherStr;
          });

          return str;
        },
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          formatter: (value: number) => {
            return dayjs(Number(value)).format(xLabel);
          },
        },
      },
      dataZoom: {
        type: 'slider',
        bottom: 20,
        height: 20,
        labelFormatter: (value: string) => {
          const time = boxplotEchart?.[0]?.dataInfoList?.[Number(value)]?.time;
          return dayjs(time).format(xLabel);
        },
      },
      yAxis: getSafeYAxis(boxplotEchart),
      series: baseSeriesOption(boxplotEchart, true),
    };
  };

  const chartOption = () => {
    return {
      grid: {
        top: 60,
        left: 40,
        right: 50,
        containLabel: true,
      },
      legend: {
        type: 'scroll',
        right: 10,
        itemGap: 16,
        data: boxplotEchart.map(s => {
          const isBar = timeResolution !== DateType.DAY && s.type === MeasurementType.INSTANT;
          return {
            name: s.dataName,
            icon: isBar
              ? 'path://M0 0 L12 0 L12 12 L0 12 Z' // 方块 (12x12)
              : 'path://M0 4 L12 4 L12 8 L0 8 Z', // 扁条 (12x4)
          };
        }),
      },
      color: ['#16DD8E', '#ffc107', '#007bff'],
      tooltip: {
        trigger: 'item',
        // confine: true,
        formatter: (params: any) => {
          const timeStr = `<div style='color: rgba(255,255,255,0.65);'>${dayjs(Number(params.name)).format(
            timeResolution === DateType.DAY ? 'YYYY-MM-DD HH:mm' : xLabel
          )}</div>`;
          if (params.componentType === 'series' && params.seriesType === 'boxplot') {
            const basic = boxplotEchart.find(i => i.dataName === params.seriesName)?.indicatorUnitName;
            const boxData: number[] = params.data;
            return `
              <div style='width: 200px;'>
                ${timeStr}
                <div style='display: flex; justify-content: space-between;'>
                  <span>
                    <span style='display:inline-block;width:8px;height:8px;background:${
                      params.color
                    };border-radius:50%;margin-right:6px;vertical-align: middle'></span>
                  ${params.seriesName}
                  </span>
                </div>
                <div style="border-top: 1px solid rgba(255,255,255,0.1); ">
                  <div style='display: flex; justify-content: space-between; margin: 3px 0;'>
                    <span style="color: rgba(255,255,255,0.8)">最大值</span>
                    <span>${boxData[5]?.toFixed(2) ?? '--'}${basic}</span>
                  </div>
                  <div style='display: flex; justify-content: space-between; margin: 3px 0;'>
                    <span style="color: rgba(255,255,255,0.8)">上四分位值</span>
                    <span>${boxData[4]?.toFixed(2) ?? '--'}${basic}</span>
                  </div>
                  <div style='display: flex; justify-content: space-between; margin: 3px 0;'>
                    <span style="color: rgba(255,255,255,0.8)">中位值</span>
                    <span>${boxData[3]?.toFixed(2) ?? '--'}${basic}</span>
                  </div>
                  <div style='display: flex; justify-content: space-between; margin: 3px 0;'>
                    <span style="color: rgba(255,255,255,0.8)">下四分位值</span>
                    <span>${boxData[2]?.toFixed(2) ?? '--'}${basic}</span>
                  </div>
                  <div style='display: flex; justify-content: space-between; margin: 3px 0;'>
                    <span style="color: rgba(255,255,255,0.8)">最小值</span>
                    <span>${boxData[1]?.toFixed(2) ?? '--'}${basic}</span>
                  </div>
                </div>
              </div>
            `;
          }

          if (params.componentType === 'series' && params.seriesType === 'line') {
            const basic = boxplotEchart.find((i: any) => i.dataName === params.seriesName);

            return `
            <div style="width:230px;border-radius: 3px; background: rgba(255,255,255,0.03);">
             ${timeStr}
              <div style='display: flex; justify-content: space-between;'>
                <span>
                  <span style='display:inline-block;width:8px;height:8px;background:${
                    params.color
                  };border-radius:50%;margin-right:6px;vertical-align: middle'></span>
                  ${params.seriesName}
                </span>
                <span>${params.data ?? '--'}${basic?.indicatorUnitName}</span>
              </div>
              <div style='display: flex; justify-content: space-between;color: rgba(255,255,255,0.7);'>
                <span>${basic?.name ?? '--'}</span>
                <span>${keepTwoNull(basic?.value)}${basic?.generalUnitName ?? '--'}</span>
              </div>
            </div>
          `;
          }
        },
      },
      xAxis: {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        data: boxplotEchart?.[0]?.dataInfoList?.map(i => i.time),
        axisLabel: {
          formatter: (value: number) => {
            return dayjs(Number(value)).format(xLabel);
          },
          margin: 8,
          lineHeight: 14,
        },
      },
      dataZoom: {
        type: 'slider',
        bottom: 20,
        height: 20,
        labelFormatter: (value: string) => {
          const time = boxplotEchart?.[0]?.dataInfoList?.[Number(value)]?.time;
          return dayjs(time).format(xLabel);
        },
      },
      yAxis: getSafeYAxis(boxplotEchart),
      series: baseSeriesOption(boxplotEchart),
    };
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div>
          <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
          <span className={styles.title}>关键指标监控</span>
        </div>
        <Space size={8}>
          <div style={{ paddingRight: 8 }}>
            <Radio.Group onChange={onChange} value={checkedList} buttonStyle="solid">
              {headTitle?.map(item => (
                <Radio.Button key={item.value} value={item.value}>
                  {item.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
          {isShow && (
            <div className={styles.compareSelect}>
              <span>对比指标：</span>
              <Select
                placeholder="请选择"
                className={styles.selectBox}
                maxCount={2}
                mode="multiple"
                value={compareIds}
                allowClear={false}
                options={options}
                onChange={onChangeSelect}
              />
            </div>
          )}
        </Space>
      </div>
      <div className={styles.content_box}>
        <div className={styles.chart_box}>
          <ResizeObserver>
            <Spin spinning={loading}>
              <EchartsWithTheme
                empty={{
                  isShowEmpty: isEmpty(datas),
                  emptyNode: <Empty style={{ height: 480, padding: '80px' }} />,
                }}
                theme={theme}
                style={{ height: 480 }}
                option={timeResolution === DateType.DAY ? dayChartOption() : chartOption()}
              />
            </Spin>
          </ResizeObserver>
        </div>
      </div>
    </div>
  );
};

export default KpiNorm;

function extractPropertyIds(list: IndicatorItem[] = []): number[] {
  return list.flatMap(item => {
    if (item.propertyId == null) return [];
    const str = String(item.propertyId);
    return str.split('-').map(id => Number(id));
  });
}
